// Shared attribute rules for the vendor item write surfaces (HUBX-8475).
//
// How the `attributes` member of an Edit body is built. It lives here, beside the other
// write rules, because the two listing rows (HUBX-8480, HUBX-8481) PUT the same full-state
// body the modal does and must not re-derive it.
//
// WHY THIS IS AN ECHO. The PUT is full state, and the server reconciles the attribute
// plane by delta: VendorItemsAttributesTable.ReconcileAsync deletes every vendor-owned row
// that is not in `desired`. "Vendor-owned" is everything except the curation names (Price
// Drop, Newly Added, Daily Specials) and the ACL-materialised Restrictions row — so a
// free-form attribute such as Visual Condition is owned, and a body that leaves it out deletes it. 
// The form renders only Condition, Warranty and Packaging, so it must send back everything else it read.
//
// The echo is safe against the reconciler in both directions: curation rows land in
// `desired` but sit outside the owned plane, so they are neither deleted nor re-inserted;
// the synthetic Restrictions row never reaches the aggregate. `attributes.*` is in
// ItemWritability.Ungated, and the gate is diff-aware, so echoing is never a change.
//
// Deliberately NOT a rendered plane: giving the vendor a control for Visual Condition was
// considered and rejected (HUBX-8475). The attribute stays invisible on every surface.
//
// Create / Clone do not come through here: a new item has nothing stored to lose, and a
// Clone sends only what the form models (see applySeed).

import { ICreateItemAttribute } from '../item.models';
import { IAttributeLookups } from './item-write-concurrency';

/** The three rendered attribute controls, by the value code each one holds. */
export interface IRenderedAttributeValues {
  condition?: string | null;
  warranty?: string | null;
  packaging?: string | null;
}

/** Build the `attributes` member of an Edit body.
 *
 *  1. The three rendered attributes, from their controls. When a control is EMPTY, the
 *     stored value is kept rather than the row being dropped. Dropping it is a deletion,
 *     not "leave it alone" (second defect in HUBX-8475, the old `if (lookup && value)`
 *     guard). This only matters where validation cannot catch it — a LOCKED control
 *     (a Seller past review, a HUBX-owned item) is disabled, and a disabled control is
 *     never validated. An EDITABLE control that is empty never gets here: its `required`
 *     validator refuses the Save first, which stays the client's gate. With nothing
 *     stored either, the member stays out and the server's own gate answers
 *     (REQUIRED_ATTRIBUTES_MISSING) — the client never invents a value.
 *
 *  2. Every stored attribute that no control renders, echoed verbatim from `stored`. An
 *     attribute is "rendered" only if its lookup RESOLVED: a Condition the
 *     /lookups/attributes catalog did not return has no control to carry it, so it is
 *     echoed like any other rather than dropped.
 *
 *  `stored` is the baseline the form was built from — `origin`, not `target`, so that after
 *  a 412 the echo carries what the store now holds. Echoing the stale value would be this
 *  Save silently reverting the other writer, the same reasoning as `tags` (HUBX-8439). */
export function editAttributes(
  values: IRenderedAttributeValues,
  lookups: IAttributeLookups,
  stored: ICreateItemAttribute[] | null | undefined
): ICreateItemAttribute[] {
  const baseline = stored ?? [];
  const attributes: ICreateItemAttribute[] = [];
  const rendered = new Set<string>();

  // Control name and lookup key are the same word for each of the three.
  for (const key of ['condition', 'warranty', 'packaging'] as const) {
    const lookup = lookups[key];
    if (!lookup) {
      continue;
    }
    rendered.add(lookup.id);
    const value = values[key] || baseline.find(a => a.attributeId === lookup.id)?.value;
    if (value) {
      attributes.push({ attributeId: lookup.id, value });
    }
  }

  for (const attribute of baseline) {
    if (!rendered.has(attribute.attributeId)) {
      attributes.push({ attributeId: attribute.attributeId, value: attribute.value });
    }
  }

  return attributes;
}
